import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signInAnonymously
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User } from '../types';

export interface AuthUser extends User {
  email?: string;
  isEmailVerified?: boolean;
  lastLoginAt?: Date;
  createdAt?: Date;
}

class AuthService {
  private currentUser: AuthUser | null = null;
  private authStateListeners: ((user: AuthUser | null) => void)[] = [];

  constructor() {
    // Listen to auth state changes
    onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        this.currentUser = await this.getUserFromFirebase(firebaseUser);
      } else {
        this.currentUser = null;
      }
      
      // Notify all listeners
      this.authStateListeners.forEach(listener => listener(this.currentUser));
    });
  }

  // Get user data from Firestore
  private async getUserFromFirebase(firebaseUser: FirebaseUser): Promise<AuthUser> {
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        id: firebaseUser.uid,
        name: userData.name || firebaseUser.displayName || 'Anonymous',
        email: firebaseUser.email || undefined,
        color: userData.color || '#3b82f6',
        avatar: userData.avatar || '👤',
        isOnline: true,
        isTyping: false,
        isAnonymous: firebaseUser.isAnonymous,
        isEmailVerified: firebaseUser.emailVerified,
        lastLoginAt: new Date(),
        createdAt: userData.createdAt?.toDate() || new Date()
      };
    } else {
      // Create new user document
      const newUser: AuthUser = {
        id: firebaseUser.uid,
        name: firebaseUser.displayName || 'Anonymous',
        email: firebaseUser.email || undefined,
        color: this.generateRandomColor(),
        avatar: '👤',
        isOnline: true,
        isTyping: false,
        isAnonymous: firebaseUser.isAnonymous,
        isEmailVerified: firebaseUser.emailVerified,
        lastLoginAt: new Date(),
        createdAt: new Date()
      };
      
      await this.createUserDocument(newUser);
      return newUser;
    }
  }

  // Create user document in Firestore
  private async createUserDocument(user: AuthUser): Promise<void> {
    await setDoc(doc(db, 'users', user.id), {
      name: user.name,
      email: user.email,
      color: user.color,
      avatar: user.avatar,
      isAnonymous: user.isAnonymous,
      createdAt: user.createdAt,
      lastLoginAt: user.lastLoginAt
    });
  }

  // Generate random color for new users
  private generateRandomColor(): string {
    const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  // Sign up with email and password
  async signUp(email: string, password: string, name: string, color?: string, avatar?: string): Promise<AuthUser> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile with display name
      await updateProfile(userCredential.user, { displayName: name });
      
      // Create user document
      const newUser: AuthUser = {
        id: userCredential.user.uid,
        name,
        email,
        color: color || this.generateRandomColor(),
        avatar: avatar || '👤',
        isOnline: true,
        isTyping: false,
        isAnonymous: false,
        isEmailVerified: false,
        lastLoginAt: new Date(),
        createdAt: new Date()
      };
      
      await this.createUserDocument(newUser);
      return newUser;
    } catch (error) {
      throw new Error(`Sign up failed: ${error}`);
    }
  }

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<AuthUser> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return await this.getUserFromFirebase(userCredential.user);
    } catch (error) {
      throw new Error(`Sign in failed: ${error}`);
    }
  }

  // Sign in with Google
  async signInWithGoogle(): Promise<AuthUser> {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      return await this.getUserFromFirebase(userCredential.user);
    } catch (error) {
      throw new Error(`Google sign in failed: ${error}`);
    }
  }

  // Sign in anonymously
  async signInAnonymously(): Promise<AuthUser> {
    try {
      const userCredential = await signInAnonymously(auth);
      return await this.getUserFromFirebase(userCredential.user);
    } catch (error) {
      throw new Error(`Anonymous sign in failed: ${error}`);
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      // Update user status to offline
      if (this.currentUser) {
        await this.updateUserStatus(this.currentUser.id, false);
      }
      
      await signOut(auth);
    } catch (error) {
      throw new Error(`Sign out failed: ${error}`);
    }
  }

  // Reset password
  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw new Error(`Password reset failed: ${error}`);
    }
  }

  // Update user profile
  async updateUserProfile(userId: string, updates: Partial<AuthUser>): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        name: updates.name,
        color: updates.color,
        avatar: updates.avatar,
        lastLoginAt: new Date()
      });
      
      // Update local user data
      if (this.currentUser && this.currentUser.id === userId) {
        this.currentUser = { ...this.currentUser, ...updates };
      }
    } catch (error) {
      throw new Error(`Profile update failed: ${error}`);
    }
  }

  // Update user online status
  async updateUserStatus(userId: string, isOnline: boolean): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        isOnline,
        lastSeenAt: new Date()
      });
    } catch (error) {
      console.error('Failed to update user status:', error);
    }
  }

  // Get current user
  getCurrentUser(): AuthUser | null {
    return this.currentUser;
  }

  // Listen to auth state changes
  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    this.authStateListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.authStateListeners.indexOf(callback);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  // Check if user is anonymous
  isAnonymous(): boolean {
    return this.currentUser?.isAnonymous || false;
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
